import {
    AtRiskReason,
    AtRiskReasonMap,
    AtRiskReasonMapWithDefaultValue,
    AtRiskReasonNames,
    AtRiskReasonValueToName,
    AuthorizationInfo,
    AuthorizationRecordType,
    AuthorizationRecordTypeMap,
    AuthorizationStatus,
    ClientInfo,
    ClientReportInfo,
    ClientReportReportStatusMap,
    ClientReportReportTypeMap,
    isClientReportStatusInAuthorized,
    isClientReportStatusInReview,
} from "./domain";
import React, {ReactElement, useEffect, useReducer, useState} from 'react';
import {ParentCaseAuthorizationListAction} from "./parentCaseAuthorizationReducer";
import {gql, useQuery} from "@apollo/client";
import {ClientReportsQueryResponse} from "./ClientProfileClientReportPage";
import {DropdownMultiSelect, LoadingSpinner} from "../../common";
import {BehaviorSubject} from "rxjs";
import {useObservable} from "../../rxsupport";
import {Pane, Tab, Tablist} from "evergreen-ui";
import {DownloadAuthorizationPDFButton} from "./authorization/AuthorizationPdfDocument";
import {ClientProfileReportTableLite} from "./ClientProfileReportTable";
import {
    AuthorizationBillingTab,
    authorizationBillingTabReducer,
    authorizationBillingTabReducerInitState,
    filterReasonsBySelectedItems,
    shouldDisableRiskReasonOption
} from "./authorization/billing/AuthorizationBillingTab";
import {hasOfferHoursAccess} from "../domain/security";
import {useCurrentUser} from "../../security";
import {ServiceType, SPLIT_DELIMITER, splitAndRemoveEmpty} from "../../placement/const";
import {AuthReportSubmissionDropdown} from "./client-profile-authorization/AuthReportSubmissionDropdown";
import {ClientProfileAuthorizationDetail} from "./ClientProfileAuthorizationDetail";
import { formatDateStr } from "common/util/date";
import { ModalTooltip } from "common/component/ModalTooltip";

export type AuthClientReportModalState = {
    showStaffingPlan$: BehaviorSubject<boolean>;
    showConsultWithCCM$: BehaviorSubject<boolean>;
    showTreatmentHold$: BehaviorSubject<boolean>;
    showAddendum$: BehaviorSubject<boolean>;
    showNoContact$: BehaviorSubject<boolean>;
    showAuthReviewNeeded$: BehaviorSubject<boolean>;
    showClientReturnTransferRequest$: BehaviorSubject<boolean>;
    showAnticipatedDischargeDateUpdate$: BehaviorSubject<boolean>;
    showMissingDocumentation$: BehaviorSubject<boolean>;
    showClientFamilyConcern$: BehaviorSubject<boolean>;
    showInsuranceNotification$: BehaviorSubject<boolean>;
    showReportStatusInquiry$: BehaviorSubject<boolean>;
}

type ClientProfileAuthorizationModalProps = {
    showModal: boolean;
    clientInfo: ClientInfo;
    authorizationGuid: string | null;
    clientGuid: string;
    authModalTabIndex: number
    submitting$: BehaviorSubject<boolean>;
    authClientReportModalState: AuthClientReportModalState;
    dispatch: React.Dispatch<ParentCaseAuthorizationListAction>;
}

const AUTHORIZATION_CLIENT_REPORTS_QUERY = gql`
    query clientReportsQuery($first: Int = 20, $after: String, $clientGuid: String, $authorizationGuid: String!) {
        authorization(
            authorizationGuid: $authorizationGuid
        ) {
            guid
            sfId

            status
            closedReason
            bhpnAuthNumber
            diagnosis
            serviceLine
            funderServiceType
            reportDueDate
            assessmentCode
            assessmentType
            recordType
            numOfTier
            startDate
            endDate

            careMediatedMidMinHours
            careMediatedMidHours

            careMediatedHighMinHours
            careMediatedHighHours

            practDirectBeneficialHours
            practDirectOptimalHours

            practMidBeneficialHours
            practMidOptimalHours

            practHighBeneficialHours
            practHighOptimalHours

            practGroupBeneficialHours
            practGroupOptimalHours

            allServiceType

            isDraft1
            status1
            serviceType1
            startDate1
            endDate1
            directCareBeneficialHour1
            midLevelBeneficialHour1
            highLevelBeneficialHour1
            groupCareBeneficialHour1
            directCareHour1
            midLevelHour1
            highLevelHour1
            groupCareHour1
            directCareCode1
            midLevelCode1
            highLevelCode1
            groupCareCode1

            status2
            isDraft2
            serviceType2
            startDate2
            endDate2
            directCareBeneficialHour2
            midLevelBeneficialHour2
            highLevelBeneficialHour2
            groupCareBeneficialHour2
            directCareHour2
            midLevelHour2
            highLevelHour2
            groupCareHour2
            directCareCode2
            midLevelCode2
            highLevelCode2
            groupCareCode2

            status3
            isDraft3
            serviceType3
            startDate3
            endDate3
            directCareBeneficialHour3
            midLevelBeneficialHour3
            highLevelBeneficialHour3
            groupCareBeneficialHour3
            directCareHour3
            midLevelHour3
            highLevelHour3
            groupCareHour3
            directCareCode3
            midLevelCode3
            highLevelCode3
            groupCareCode3

            status4
            isDraft4
            serviceType4
            startDate4
            endDate4
            directCareBeneficialHour4
            midLevelBeneficialHour4
            highLevelBeneficialHour4
            groupCareBeneficialHour4
            directCareHour4
            midLevelHour4
            highLevelHour4
            groupCareHour4
            directCareCode4
            midLevelCode4
            highLevelCode4
            groupCareCode4

            status5
            isDraft5
            serviceType5
            startDate5
            endDate5
            directCareBeneficialHour5
            midLevelBeneficialHour5
            highLevelBeneficialHour5
            groupCareBeneficialHour5
            directCareHour5
            midLevelHour5
            highLevelHour5
            groupCareHour5
            directCareCode5
            midLevelCode5
            highLevelCode5
            groupCareCode5

            status6
            isDraft6
            serviceType6
            startDate6
            endDate6
            directCareBeneficialHour6
            midLevelBeneficialHour6
            highLevelBeneficialHour6
            groupCareBeneficialHour6
            directCareHour6
            midLevelHour6
            highLevelHour6
            groupCareHour6
            directCareCode6
            midLevelCode6
            highLevelCode6
            groupCareCode6

            latestSubmittedClientReport {
                sfOperationCaseNumber
                authorizedDate
                reportStatus
                reportType
                sfCloseReason
                sfCloseDate
            }
            latestSubmittedClientReportByReportType {
                guid
                reportType
                authorizedDate
                reportStatus
                sfOperationCaseNumber
                submittedAt
                sfCloseReason
                sfCloseDate
            }

            hasAddendumSubmittedRecently
            hasStaffingPlanSubmittedRecently
            hasStaffingNonRecurringSubmittedConsecutively

            billedMonths
            therapyTreatmentUnitsPer,
            therapyTreatmentUnitType,
            therapyTreatmentUnit,
            diagnosticEvaluationProcedureCode,
            therapyTreatmentProcedureCode,
            funderAuthorizationRecordType,
            funderAuthSpeciality

            pendingAuthorizationsByClient {
                guid
                sfId
                bhpnAuthNumber
                startDate
                endDate
            }
            draftAuthFromApprovedReportByClient {
                guid
                sfId
                bhpnAuthNumber
                startDate
                endDate
            }
        }
        clientReportsQuery(
            first: $first
            after: $after
            clientGuid: $clientGuid
            authorizationGuid: $authorizationGuid
        ) {
            edges {
                cursor
                node {
                    guid
                    reportType
                    reportStatus
                    authorizedDate
                    authorizedBy
                    clientProfile {
                        name
                        medicalRecordNumber
                    }
                    clientReportFiles {
                        guid
                        fileName
                        fileExtension
                        downloadUrl
                    }
                    clientReportFilesBySfOperationCase {
                        guid
                        fileName
                        fileExtension
                        downloadUrl
                    }
                    submittedAt
                    submittedBy {
                        id
                        guid
                        firstName
                        lastName
                    }
                    clientReportNotifyUsers {
                        id
                        guid
                        firstName
                        lastName
                    }
                    newAuthorizationStartDate
                    finalDiagnosticContactDate
                    vinelandIncluded
                    firstTreatmentOfferedDate
                    lastDayOfServiceBilled
                    anticipatedDischargeDate

                    serviceRecommended
                    serviceType
                    caregiverMidLevelHoursMin
                    caregiverMidLevelHoursMax
                    caregiverHighLevelHoursMin
                    caregiverHighLevelHoursMax
                    practitionerParaprofessionalHoursMin
                    practitionerParaprofessionalHoursMax
                    practitionerMidLevelHoursMin
                    practitionerMidLevelHoursMax
                    practitionerHighLevelHoursMin
                    practitionerHighLevelHoursMax
                    practitionerGroupCareHoursMin
                    practitionerGroupCareHoursMax
                    offeredParaprofessionalHours
                    offeredMidLevelHours
                    offeredHighLevelHours
                    offeredGroupCareHours

                    clinicalRationale
                    nameAndTitle
                    licenseNumber
                    treatmentOffered
                    sfOperationCaseNumber
                    sfOperationCaseId
                    sfOperationCaseRptStatus
                    sfCloseReason
                    sfCloseDate
                    sfLastModifiedDate

                    authorization {
                        bhpnAuthNumber
                        reportDueDate
                    }
                }
            }
            totalElements
            pageInfo {
                hasPreviousPage
                endCursor
                hasNextPage
                startCursor
            }
        }
    }
`

enum AuthorizationTab {
    Detail = "Detail",
    Submitted_Report = "Submitted Reports",
    Billing = "Billing"
}

export function ClientProfileAuthorizationModal({
                                                    clientInfo,
                                                    showModal,
                                                    authorizationGuid,
                                                    submitting$,
                                                    dispatch,
                                                    clientGuid,
                                                    authModalTabIndex,
                                                    authClientReportModalState,
                                                }: ClientProfileAuthorizationModalProps) {
    const success = useObservable(submitting$, false);
    let tabIndex = authModalTabIndex;

    const defaultTabList = [AuthorizationTab.Detail, AuthorizationTab.Submitted_Report];
    const [tabs, setTabs] = useState(defaultTabList);
    if (tabIndex > tabs.length - 1) {
        tabIndex = 0;
    }

    const currentUser = useCurrentUser();
    const offerHoursAccess = hasOfferHoursAccess(currentUser);
    const [billingTabState, billingTabDispatch] = useReducer(authorizationBillingTabReducer, authorizationBillingTabReducerInitState());

    const {
        loading,
        data,
        error,
        refetch
    } = useQuery<{
        clientReportsQuery: ClientReportsQueryResponse,
        authorization: AuthorizationInfo
    }, {}>(AUTHORIZATION_CLIENT_REPORTS_QUERY, {
        skip: authorizationGuid === null,
        variables: {
            clientGuid: clientGuid,
            authorizationGuid: authorizationGuid ?? "",
            first: 100 // TODO: implement correct pagination instead of using hard code page size
        },
        onCompleted(data) {
            const hideBilling = !offerHoursAccess
                || AuthorizationRecordTypeMap.get(data?.authorization?.recordType ?? "") === AuthorizationRecordType.ABA_Assessment_Authorization
                || AuthorizationRecordTypeMap.get(data?.authorization?.recordType ?? "") === AuthorizationRecordType.Diagnostic_Evaluation_Authorization
            const visibleTabs = hideBilling ? defaultTabList : [...defaultTabList, AuthorizationTab.Billing];
            setTabs(visibleTabs);
        }
    })

    useEffect(() => {
        if (success) {
            refetch();
            submitting$.next(false);
        }
    }, [refetch, submitting$, success])

    if (error) {
        console.log("failed to get client report detail")
    }

    const authorization = data?.authorization;
    const isAssessmentAuth = [AuthorizationRecordType.ABA_Assessment_Authorization, AuthorizationRecordType.Psychotherapy_Assessment].includes(AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") as AuthorizationRecordType);
    const isDEAuth = AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") === AuthorizationRecordType.Diagnostic_Evaluation_Authorization;
    const isTherapyAuth = AuthorizationRecordTypeMap.get(authorization?.recordType ?? "") === AuthorizationRecordType.Therapy_Treatment_Authorization;
    let clientReports = new Array<ClientReportInfo>();
    if (data?.clientReportsQuery) {
        clientReports = data?.clientReportsQuery.edges.map((edge) => edge.node);
    }


    let notShown = [
        "Change_Hour_Addendum", "Full_Addendum", "Consult_With_CCM"
    ];
    if (authorization && authorizationNotContainParentLedOrParentTraining(authorization)) {
        notShown = [...notShown, "Switched_to_Caregiver_Mediated_Flex_Tx"]
    }
    notShown = notShown.concat(hideReasonsIfStuffsAreNotRecentlySubmitted(authorization));
    notShown = notShown.concat(hideReasonsIfStaffingNonRecurringSubmittedConsecutively(authorization));

    let visibleReasons = AtRiskReasonNames.filter((r) => notShown.indexOf(r) === -1);
    const namesMap = AtRiskReasonMapWithDefaultValue;
    visibleReasons.sort((r1, r2) => {
        const n1 = namesMap.get(r1) || '';
        const n2 = namesMap.get(r2) || '';
        return n1.localeCompare(n2)
    });

    const preventSelectOptionAndMessage = new Map<string, string>();
    if (authorization?.status === AuthorizationStatus.Pending) {
        preventSelectOptionAndMessage.set("Report", "Client Reports can only be submitted against active authorizations. Please locate the active authorization for this client and resubmit.");
        preventSelectOptionAndMessage.set("Change Hours Addendum", "Change Hours Addendums can only be submitted against active authorizations. Please locate the active authorization for this client and resubmit.");
    }
        

    return (
        <div
            className={(showModal ? "flex " : "hidden ") + "overflow-y-auto overflow-x-hidden fixed z-50 justify-center items-start inset-0"}
            style={{backgroundColor: "rgba(0,0,0,0.4)"}}>
            {/* BHPNPP-3072: fixed (viewport) overlay + flex centring so the modal height no longer depends on the
                Client Details top bar (BHPNPP-3030). min-h-full lets a tall auth scroll from the top instead of
                clipping the header. */}
            <div className="flex items-center justify-center min-h-full w-full px-4 py-6">
                <div className="relative w-full">
                {
                    loading && <LoadingSpinner spacing={20} height={"60vh"}/>
                }
                {
                    !loading && !authorization &&
                    <div className="relative bg-white shadow dark:bg-gray-700">
                        <div className="flex justify-between items-start p-2 border-b dark:border-gray-600">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                {`Authorization not found`}
                            </h3>
                            <button type="button"
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                    onClick={() => dispatch({type: "HIDE_MODAL"})}
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd"
                                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                          clipRule="evenodd"/>
                                </svg>
                            </button>
                        </div>

                        <Pane className="space-y-6 authorization-detail-modal-content-height">
                        </Pane>
                    </div>
                }
                {authorization && <>
                    <div className="relative bg-white shadow dark:bg-gray-700 flex flex-col max-h-[90vh]">
                        <div className="flex-shrink-0 flex justify-between items-start p-2 border-b dark:border-gray-600">
                            <div className="flex items-center">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                    {`Authorization #${authorization?.bhpnAuthNumber}`}
                                </h3>
                                <LatestReportInformation latestSubmittedClientReport={authorization?.latestSubmittedClientReport}/>
                            </div>
                            <button type="button"
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                    onClick={() => dispatch({type: "HIDE_MODAL"})}
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd"
                                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                          clipRule="evenodd"/>
                                </svg>
                            </button>
                        </div>
                        <div className="flex-shrink-0 flex justify-between pb-2">
                            <div className="flex items-center">
                                <Tablist paddingTop={4} marginX={4} marginBottom={8} flexBasis={300}>
                                    {
                                        tabs.map((tab, index) => (
                                            <Tab
                                                key={tab}
                                                id={tab.toString()}
                                                onSelect={() => dispatch({
                                                    type: 'SET_AUTH_MODAL_TAB_INDEX',
                                                    index: index
                                                })}
                                                isSelected={index === tabIndex}
                                                aria-controls={`panel-${tab}`}
                                                height='36px'
                                                padding='8px'
                                            >
                                    <span
                                        className={`text-sm ${index === tabIndex ? 'text-atlantis-200 font-medium' : 'text-gray-600 font-normal'}`}>
                                        {tab}
                                    </span>
                                            </Tab>
                                        ))

                                    }
                                </Tablist>
                            </div>
                            <ToolbarButton tab={tabs[authModalTabIndex]}
                                           downloadAuthBtn={
                                               <div className="pt-1 pr-2">
                                                   <DownloadAuthorizationPDFButton authorization={authorization}
                                                                                   clientInfo={clientInfo}/>
                                               </div>
                                           }
                                           showReportBtn={
                                               <div className="pt-1 pr-2">
                                                   <AuthReportSubmissionDropdown
                                                       isDEAuth={isDEAuth}
                                                       isTherapyAuth={isTherapyAuth}
                                                       isAssessmentAuth={isAssessmentAuth}
                                                       modalState={authClientReportModalState}
                                                       dispatch={dispatch}
                                                       preventSelectOptionAndMessage={preventSelectOptionAndMessage}
                                                   />
                                               </div>
                                           }
                                           billingActionBtn={
                                               <div
                                                   className={billingTabState.selectedGuids.length > 0 ? "" : "hidden"}>
                                                   <DropdownMultiSelect
                                                       field={'atRiskReason'}
                                                       options={filterReasonsBySelectedItems(visibleReasons, billingTabState.selectedItems)}
                                                       placeholder={'-- At Risk Reason --'}
                                                       full={false}
                                                       value={billingTabState.atRiskReasons.join(SPLIT_DELIMITER)}
                                                       labelMapperFunc={(val) => AtRiskReasonMap.get(val)!!}
                                                       disableFunc={(checked, option) => shouldDisableRiskReasonOption(checked, option, billingTabState.selectedItems)}
                                                       setValue={(val) => {
                                                           billingTabDispatch({
                                                               type: "SET_AT_RISK_REASONS",
                                                               atRiskReasons: splitAndRemoveEmpty(val)
                                                           })
                                                       }}
                                                   />
                                               </div>
                                           }
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto px-2">
                        <Pane className="space-y-6" aria-labelledby="Detail" role="tabpanel"
                              aria-hidden={0 !== tabIndex}
                              display={0 === tabIndex ? 'block' : 'none'}>
                            <ClientProfileAuthorizationDetail authorization={authorization}
                                                              isAssessmentAuth={isAssessmentAuth}/>
                        </Pane>
                        <Pane className="space-y-6 authorization-detail-modal-content-height" aria-labelledby="Detail"
                              role="tabpanel"
                              aria-hidden={1 !== authModalTabIndex}
                              display={1 === authModalTabIndex ? 'block' : 'none'}>
                            {loading ? <LoadingSpinner spacing={10} height={"100%"}/> :
                                <ClientProfileReportTableLite reports={clientReports} selected={null}
                                                              hideModalFunc={() => dispatch({type: 'HIDE_VIEW_REPORT_MODAL'})}
                                                              loading={loading}
                                                              setSelectedFunc={(report) => dispatch({
                                                                  type: 'SHOW_VIEW_REPORT_MODAL',
                                                                  selectedReport: report
                                                              })}/>}
                        </Pane>
                        <Pane className="space-y-6 authorization-detail-modal-content-height" aria-labelledby="Detail"
                              role="tabpanel"
                              aria-hidden={2 !== authModalTabIndex}
                              display={2 === authModalTabIndex ? 'block' : 'none'}>
                            <AuthorizationBillingTab authorization={authorization}
                                                     currentState={billingTabState}
                                                     dispatch={billingTabDispatch}
                                                     clientGuid={clientGuid}
                                                     onRiskReasonsSubmitted={refetch}
                            />
                        </Pane>
                        </div>
                    </div>

                </>}
                </div>
            </div>
        </div>
    );
}

type ToolbarButtonProps = {
    tab: AuthorizationTab;
    downloadAuthBtn: ReactElement;
    showReportBtn: ReactElement;
    billingActionBtn: ReactElement;
}
const ToolbarButton = ({tab, downloadAuthBtn, showReportBtn, billingActionBtn}: ToolbarButtonProps) => {
    const showDownloadAuthBtn = [AuthorizationTab.Detail].includes(tab)
    const showSubmittedReportBtn = [AuthorizationTab.Detail, AuthorizationTab.Submitted_Report].includes(tab)
    const showBillingActionBtn = [AuthorizationTab.Billing].includes(tab);
    return (
        <div className="flex items-center">
            <div className={showDownloadAuthBtn ? "" : "hidden"}>{downloadAuthBtn}</div>
            <div className={showSubmittedReportBtn ? "" : "hidden"}>{showReportBtn}</div>
            <div className={showBillingActionBtn ? "flex mr-2" : "hidden"}>{billingActionBtn}</div>
        </div>
    );
}

const authorizationNotContainParentLedOrParentTraining = (auth: AuthorizationInfo | undefined) => {
    return !auth?.allServiceType
        .some(a => [ServiceType.ThreeTierOrParentTraining, ServiceType.ThreeTierORParentLed].includes(a as ServiceType))
}

// BHPNPP-1957
const hideReasonsIfStuffsAreNotRecentlySubmitted = (authorization: AuthorizationInfo | undefined) => {
    const notShown: string[] = [];
    if (authorization?.hasAddendumSubmittedRecently === false) {
        notShown.push(AtRiskReasonValueToName.get(AtRiskReason.Addendum_recently_submitted)! as string);
    }
    if (authorization?.hasStaffingPlanSubmittedRecently === false) {
        notShown.push(AtRiskReasonValueToName.get(AtRiskReason.Staffing_Plan_recently_submitted)! as string);
    }
    return notShown;
}

// BHPNPP-2008
function hideReasonsIfStaffingNonRecurringSubmittedConsecutively(authorization: AuthorizationInfo | undefined) {
    const notShown: string[] = [];
    if (authorization?.hasStaffingNonRecurringSubmittedConsecutively === true) {
        notShown.push(AtRiskReasonValueToName.get(AtRiskReason.Staffing_Non_recurring)! as string);
    }
    return notShown;
}

function LatestReportInformation({latestSubmittedClientReport}: {latestSubmittedClientReport?: ClientReportInfo}) {
    if (!latestSubmittedClientReport) {
        return null;
    }
    const inReviewStatus =  isClientReportStatusInReview(latestSubmittedClientReport.reportStatus);
    const inAuthorizedStatus =  isClientReportStatusInAuthorized(latestSubmittedClientReport.reportStatus);
    const statusCss = inReviewStatus ? "bg-orange-600 text-white" : inAuthorizedStatus ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-800";

    return (
        <div className="flex flex-row items-center bg-gray-50 pl-1 text-sm text-gray-600 align-center">
            <div className="ml-2 font-bold pr-2">Latest Report {latestSubmittedClientReport.sfOperationCaseNumber ? "#" + latestSubmittedClientReport.sfOperationCaseNumber : ""}</div>
            <div className="mr-2 flex items-center align-middle">
                <div className="flex items-center text-xs">
                    <div className="font-bold">[ </div>
                    <div className="font-bold">Type:</div>
                    <div className="font-semibold px-2 py-1 bg-atlantis-300 rounded-md text-gray-100 ml-1">
                        {ClientReportReportTypeMap.get(latestSubmittedClientReport.reportType)}
                    </div>
                    <div className="mx-1 font-bold">|</div>
                    <div className="font-bold">Status:</div>
                    <div className={`font-semibold px-2 py-1 ${statusCss} rounded-md ml-1`}>
                        {ClientReportReportStatusMap.get(latestSubmittedClientReport.reportStatus)}
                    </div>
                    {
                        latestSubmittedClientReport.sfCloseReason != null &&
                        <ModalTooltip content={latestSubmittedClientReport.sfCloseReason} />
                    }
                    {
                        latestSubmittedClientReport.sfCloseDate != null &&
                        <>
                            <div className="mx-1 font-bold">|</div>
                            <span className="text-gray-600 font-bold mr-1">Closed Date:</span>
                            <span className="font-semibold">
                                {formatDateStr(latestSubmittedClientReport.sfCloseDate) !== '' ? formatDateStr(latestSubmittedClientReport.sfCloseDate) : "None"}
                            </span>
                        </>
                    }
                    {
                        latestSubmittedClientReport.authorizedDate != null &&
                        <>
                            <div className="mx-1 font-bold">|</div>
                            <span className="text-gray-600 font-bold mr-1">Authorized Date:</span>
                            <span className="font-semibold">
                                {formatDateStr(latestSubmittedClientReport.authorizedDate) !== '' ? formatDateStr(latestSubmittedClientReport.authorizedDate) : "None"}
                            </span>
                        </>
                    }
                    <div className="pl-1 font-bold">]</div>
                </div>
            </div>
        </div>
    );
}
